<template>
  <div :class="b()">
    <c-breadcrumb />
    <h1>
      {{ $t('l-contact-list.title') }}
    </h1>
    <aside :class="b('filters')">
      <h2 v-if="showResults && (selectedDivision || selectedDepartment) && groupedContactPersons.length === 1"
          :class="b('filtered-by')"
      >
        {{ groupedContactPersons[0]?.group.title }}
      </h2>
      <div :class="b('facets')">
        <c-google-places-autocomplete v-model="placesQuery"
                                      :class="b('facet')"
                                      :label="$t('global.location')"
                                      :initial-postal-code="postalCode"
                                      :disabled="runningRequests.apiGetFilteredContacts"
                                      @change="applyLocationFilter"
                                      @reset="resetLocationFilter"
        />
        <e-label v-if="hasDivisionFilter && contactListStore.divisions.length"
                 :class="b('facet', { division: true })"
                 :name="$t('global.division')"
        >
          <e-select v-model="divisionCode"
                    :options="contactListStore.divisions"
                    :progress="runningRequests.apiGetFilteredContacts"
                    :disabled="!postalCode || runningRequests.apiGetFilteredContacts"
                    label-field="name"
                    value-field="code"
                    has-selectable-placeholder
          />
        </e-label>
        <e-label v-if="hasDepartmentFilter && contactListStore.departments.length"
                 :class="b('facet')"
                 :name="$t('global.department')"
        >
          <e-select v-model="departmentCode"
                    :options="contactListStore.departments"
                    :progress="runningRequests.apiGetFilteredContacts"
                    :disabled="!postalCode || runningRequests.apiGetFilteredContacts"
                    label-field="name"
                    value-field="code"
                    has-selectable-placeholder
          />
        </e-label>
      </div>
    </aside>
    <section :class="b('results')">
      <div v-if="!showResults">
        {{ $t('l-contact-list.noLocationMessage') }}
      </div>
      <div v-else
           v-for="contactGroup in groupedContactPersons"
           :key="contactGroup.group.title"
           :class="b('results-block')"
      >
        <h3 v-if="groupedContactPersons.length > 1 && contactGroup.group.title">
          {{ contactGroup.group.title }}
        </h3>
        <ul :class="b('contact-person-list')">
          <li v-for="contactPerson in contactGroup.contactPersons"
              :key="contactPerson.identifier"
              :class="b('contact-person-item')"
          >
            <c-contact-person :contact-person="contactPerson"
                              :show-restricted-fields="contactListStore.showRestrictedFields"
                              :picture-sizes="pictureSizes"
            />
          </li>
        </ul>
      </div>

    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import cBreadcrumb from '@/components/c-breadcrumb.vue';
  import cGooglePlacesAutocomplete from '@/components/c-google-places-autocomplete.vue';
  import eSelect from '@/elements/e-select.vue';
  import eLabel from '@/elements/e-label.vue';
  import cContactPerson from '@/components/c-contact-person.vue';
  import useContactListStore from '@/stores/contact-list';
  import setActiveLayout from '@/compositions/set-active-layout';
  import { Layout } from '@/setup/globals';
  import { ContactDivision } from '@/types/contact-division';
  import { ContactDepartment } from '@/types/contact-department';
  import { ContactPerson } from '@/types/contact-person';
  import { ImageSizes } from '@/types/sizes';

  const layout = Layout.ContactList;

  type GroupedContactPersons = {
    group: {
      title: string;
    };
    contactPersons: ContactPerson[];
  }

  type Setup = {
    contactListStore: ReturnType<typeof useContactListStore>;
    pictureSizes: ImageSizes;
  }

  type Data = {
    placesQuery?: string;
    divisionCode?: string;
    departmentCode?: string;
    postalCode?: string;
    countryCode?: string;
    runningRequests: {
      apiGetFilteredContacts: boolean;
    };
  }

  /**
   * Renders a list of contact tiles.
   */
  export default defineComponent({
    name: layout,
    components: {
      cContactPerson,
      eLabel,
      eSelect,
      cGooglePlacesAutocomplete,
      cBreadcrumb,
    },

    // components: {},

    // props: {},
    // emits: {},

    setup(): Setup {
      setActiveLayout(layout);

      return {
        contactListStore: useContactListStore(),
        pictureSizes: {
          xxs: 425,
          xs: 712,
          sm: 458,
          md: 368,
          fallback: 340,
        },
      };
    },
    data(): Data {
      const contactListStore = useContactListStore(); // this.contactListStore was not supported by TS...

      return {
        placesQuery: undefined,
        divisionCode: contactListStore.divisions.find(division => division.selected)?.code,
        departmentCode: contactListStore.departments.find(division => division.selected)?.code,
        postalCode: contactListStore.postalCode,
        countryCode: undefined,
        runningRequests: {
          apiGetFilteredContacts: false,
        },
      };
    },

    computed: {
      showResults(): boolean {
        const hasLocationValue = !!this.contactListStore.getActiveRegionCode;

        if (!this.contactListStore.contactPersons.length) {
          return false;
        }

        if (hasLocationValue && this.hasDivisionFilter && this.divisionCode) {
          return true;
        }

        if (hasLocationValue && this.hasDepartmentFilter && this.departmentCode) {
          return true;
        }

        return hasLocationValue;
      },

      hasDivisionFilter(): boolean {
        return this.contactListStore.visibleFilters.includes('division');
      },

      hasDepartmentFilter():boolean {
        return this.contactListStore.visibleFilters.includes('department');
      },

      selectedDivision(): ContactDivision | undefined {
        return this.divisionCode
          ? this.contactListStore.divisions.find(({ code }) => code === this.divisionCode)
          : undefined;
      },

      selectedDepartment(): ContactDepartment | undefined {
        return this.departmentCode
          ? this.contactListStore.departments.find(({ code }) => code === this.departmentCode)
          : undefined;
      },

      unassignedContactPersons(): ContactPerson[] {
        return this.contactListStore.contactPersons
          .filter(({ division, department }) => !division && !department);
      },

      divisionContactPersons(): GroupedContactPersons[] {
        return this.contactListStore.divisions.reduce((accumulator, division) => {
          if (this.divisionCode && this.divisionCode !== division.code) {
            return accumulator;
          }

          const contactPersons = this.contactListStore.contactPersons
            .filter(contactPerson => division.code === contactPerson.division);

          if (contactPersons.length) {
            accumulator.push({
              group: {
                title: division.name,
              },
              contactPersons,
            });
          }

          return accumulator;
        }, [] as GroupedContactPersons[]);
      },

      departmentContactPersons(): GroupedContactPersons[] {
        return this.contactListStore.departments.reduce((accumulator, department) => {
          if (this.departmentCode && this.departmentCode !== department.code) {
            return accumulator;
          }

          const contactPersons = this.contactListStore.contactPersons
            .filter(contactPerson => department.code === contactPerson.department);

          if (contactPersons.length) {
            accumulator.push({
              group: {
                title: department.name,
              },
              contactPersons,
            });
          }

          return accumulator;
        }, [] as GroupedContactPersons[]);
      },

      groupedContactPersons(): GroupedContactPersons[] {
        const groupedContacts = [];

        if (!this.departmentCode && !this.divisionCode) {
          groupedContacts.push({
            group: {
              title: '',
            },
            contactPersons: this.unassignedContactPersons,
          });
        }

        if (this.hasDivisionFilter && (this.divisionCode || !this.departmentCode)) {
          groupedContacts.push(...this.divisionContactPersons);
        }

        if (this.hasDepartmentFilter && (this.departmentCode || !this.divisionCode)) {
          groupedContacts.push(...this.departmentContactPersons);
        }

        return groupedContacts;
      },
    },
    watch: {
      divisionCode(): void {
        this.requestContactPersons();
      },

      departmentCode(): void {
        this.requestContactPersons();
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      resetLocationFilter() {
        this.postalCode = undefined;
        this.countryCode = undefined;
        this.divisionCode = undefined;

        this.requestContactPersons();
      },

      applyLocationFilter(place: google.maps.GeocoderResult) {
        this.postalCode = place.address_components
          ?.find(({ types }) => types.includes('postal_code'))
          ?.long_name;
        this.countryCode = place.address_components
          ?.find(({ types }) => types.includes('country'))
          ?.short_name;

        this.requestContactPersons();
      },

      requestContactPersons(): void {
        const {
          postalCode,
          countryCode,
        } = this;
        const regionCode = this.contactListStore.getActiveRegionCode;

        if (!regionCode && (!postalCode || !countryCode)) {
          return;
        }

        this.runningRequests.apiGetFilteredContacts = true;

        this.contactListStore.apiGetFilteredContacts({
          postalcode: postalCode,
          countrycode: countryCode,
          region: postalCode && countryCode ? undefined : regionCode,
          division: this.divisionCode,
          department: this.departmentCode,
        }).then(() => {
          this.updateUrl();
        }).finally(() => {
          this.runningRequests.apiGetFilteredContacts = false;
        });
      },

      updateUrl() {
        window.history.pushState(null, '', this.contactListStore.activeUrl);
        this.$gtm.pushHistoryChange();
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .l-contact-list {
    $this: &;

    &__filters {
      @include mixins.grid();

      align-items: end;
      margin-bottom: variables.$spacing--30;
    }

    &__filtered-by {
      grid-column: span 12;
      order: 1;
      margin: 0;

      @include mixins.media(lg) {
        grid-column: span 3;
        order: initial;
      }
    }

    &__facets {
      grid-column: span 12;

      @include mixins.media(sm) {
        display: flex;
        gap: variables.$spacing--20;
        justify-content: flex-end;
      }

      @include mixins.media(lg) {
        grid-column: 4 / span 9;
      }
    }

    &__facet {
      margin-bottom: variables.$spacing--20;

      @include mixins.media(sm) {
        width: percentage(math.div(1, 3));
        margin-bottom: 0;
      }
    }

    &__contact-person-list {
      display: grid;
      gap: variables.$spacing--30;

      @include mixins.media(sm) {
        @include mixins.grid();

        grid-row-gap: variables.$spacing--40;
      }
    }

    &__contact-person-item {
      grid-column: span 6;

      @include mixins.media(md) {
        grid-column: span 4;
      }

      @include mixins.media(lg) {
        grid-column: span 3;
      }

      &:not(:last-of-type) {
        @include mixins.media($down: xs) {
          padding-bottom: variables.$spacing--30;
          border-bottom: 1px solid variables.$color-grayscale--0;
        }
      }

      .c-contact-person {
        height: 100%;
      }
    }

    &__results-block {
      & + & {
        margin-top: variables.$spacing--60;
      }
    }
  }
</style>
